import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  // Base — shared across all variants and sizes
  [
    "cursor-pointer inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap",
    "rounded-lg border border-transparent font-medium",
    "transition-all duration-100 select-none outline-none",
    "focus-visible:ring-3 focus-visible:ring-primary/25 focus-visible:border-primary",
    "active:scale-[0.98]",
    "disabled:pointer-events-none disabled:opacity-45 disabled:cursor-not-allowed",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5",
  ],
  {
    variants: {
      variant: {
        // ── Primary ──────────────────────────────────────────────
        default:
          "bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-active",

        // ── Secondary (outlined) ──────────────────────────────────
        secondary:
          "bg-transparent text-primary border border-primary hover:bg-primary-light disabled:text-primary-disabled disabled:border-primary-disabled",

        // ── Ghost ─────────────────────────────────────────────────
        ghost:
          "bg-transparent text-primary hover:bg-primary-light disabled:text-primary-disabled",

        // ── Destructive ───────────────────────────────────────────
        destructive:
          "bg-error text-primary-foreground hover:bg-error/90 active:bg-error/80 focus-visible:ring-error/25 focus-visible:border-error",

        // ── Link ──────────────────────────────────────────────────
        link: "text-primary underline-offset-4 hover:underline",

        // ── Outline (generic, e.g. dismiss) ───────────────────────
        outline:
          "border border-border bg-surface text-foreground hover:bg-background",
      },
      size: {
        // Matches design system height spec exactly
        sm: "h-9 px-4 text-[13px]",
        default: "h-11 px-5 text-sm",
        lg: "h-12 px-6 text-[15px]",

        // Icon-only layouts (square)
        "icon-sm": "size-9",
        "icon-default": "size-11",
        "icon-lg": "size-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Renders button full-width — use for primary actions on mobile */
  fullWidth?: boolean;
}

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  fullWidth = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : "button";

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(
        buttonVariants({ variant, size }),
        fullWidth && "w-full",
        className,
      )}
      {...props}
    />
  );
}

export { Button, buttonVariants };
